import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import './Descargas.css';

const API = 'https://us-central1-inprojuy-f1173.cloudfunctions.net/app/api/descargas';

const FILTROS_TIPO = [
  { label: 'Todos', value: 'todos' },
  { label: '📕 PDFs', value: 'pdf' },
  { label: '🖼 Imágenes', value: 'image' },
];

const CATEGORIA_LABELS = {
  manuales: 'Manuales / Documentos',
  placas: 'Placas',
  flyers: 'Flyers',
  info: 'Información / Infografías',
  otros: 'Otros',
};

function fmtSize(kb) {
  if (!kb) return '';
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export default function Descargas() {
  const [data, setData] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [preview, setPreview] = useState(null); // { url, titulo, archivoUrl, nombreOriginal }

  useEffect(() => {
    cargar();
  }, []);

  async function cargar() {
    setCargando(true);
    try {
      const res = await axios.get(API);
      setData(res.data);
    } catch (e) {
      console.error('Error al cargar descargas:', e);
      setData([]);
    } finally {
      setCargando(false);
    }
  }

  const categoriasDisponibles = useMemo(() => {
    const set = new Set(data.map((d) => d.categoria || 'otros'));
    return [{ label: 'Todas las categorías', value: 'todas' }, ...Array.from(set).map((c) => ({ label: CATEGORIA_LABELS[c] || c, value: c }))];
  }, [data]);

  const items = data.filter((d) => {
    if (filtroTipo !== 'todos' && d.archivo?.tipo !== filtroTipo) return false;
    if (filtroCategoria !== 'todas' && (d.categoria || 'otros') !== filtroCategoria) return false;
    return true;
  });

  async function trackDescarga(id) {
    // Fire-and-forget — no bloquea la descarga
    try { await axios.post(`${API}/${id}/contar`); } catch {}
  }

  // Cerrar modal con tecla Escape
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => { if (e.key === 'Escape') setPreview(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  return (
    <div className="descargas-page">
      <div className="descargas-header">
        <h1>Material Descargable</h1>
        <p>Recursos en alta resolución para imprimir en agencias</p>
      </div>

      <div className="descargas-filtros">
        <div className="descargas-tipos">
          {FILTROS_TIPO.map((f) => (
            <button
              key={f.value}
              className={`descargas-chip ${filtroTipo === f.value ? 'activo' : ''}`}
              onClick={() => setFiltroTipo(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          className="descargas-select"
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)}
        >
          {categoriasDisponibles.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {cargando ? (
        <div className="descargas-empty">
          <div className="descargas-spinner" />
          <p>Cargando material…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="descargas-empty">
          <p>No hay descargas que coincidan con los filtros.</p>
        </div>
      ) : (
        <div className="descargas-grid">
          {items.map((item) => (
            <ItemDescarga
              key={item.id}
              item={item}
              onDescargar={() => trackDescarga(item.id)}
              onPreview={(p) => setPreview(p)}
            />
          ))}
        </div>
      )}

      {preview && (
        <div className="descargas-lightbox" onClick={() => setPreview(null)}>
          <button className="descargas-lightbox-close" onClick={() => setPreview(null)} aria-label="Cerrar">
            <i className="pi pi-times" />
          </button>
          <div className="descargas-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={preview.url} alt={preview.titulo} className="descargas-lightbox-img" />
            <div className="descargas-lightbox-footer">
              <div className="descargas-lightbox-title">{preview.titulo}</div>
              <a
                className="descargas-lightbox-btn"
                href={preview.archivoUrl}
                download={preview.nombreOriginal || preview.titulo}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDescarga(preview.id)}
              >
                <i className="pi pi-download" /> Descargar
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemDescarga({ item, onDescargar, onPreview }) {
  const esPdf = item.archivo?.tipo === 'pdf';
  const previewUrl = item.thumbnail?.url || (item.archivo?.tipo === 'image' ? item.archivo?.url : null);
  const puedePreview = !esPdf && !!previewUrl;

  function handleThumbClick() {
    if (!puedePreview) return;
    onPreview({
      id: item.id,
      url: item.archivo?.url || previewUrl,  // usar la imagen original para fullsize
      titulo: item.titulo,
      archivoUrl: item.archivo?.url,
      nombreOriginal: item.archivo?.nombreOriginal,
    });
  }

  return (
    <article className="descarga-card">
      <div
        className={`descarga-thumb ${puedePreview ? 'descarga-thumb-clickable' : ''}`}
        onClick={handleThumbClick}
        title={puedePreview ? 'Click para ver en grande' : ''}
      >
        {previewUrl ? (
          <>
            <img src={previewUrl} alt={item.titulo} loading="lazy" />
            {puedePreview && (
              <div className="descarga-thumb-overlay">
                <i className="pi pi-search-plus" />
              </div>
            )}
          </>
        ) : (
          <div className="descarga-thumb-pdf">
            <i className="pi pi-file-pdf" />
            <span>PDF</span>
          </div>
        )}
      </div>

      <div className="descarga-body">
        <h3 className="descarga-titulo">{item.titulo}</h3>
        {item.descripcion && <p className="descarga-descripcion">{item.descripcion}</p>}
        <div className="descarga-meta">
          <span className={`descarga-tag ${esPdf ? 'tag-pdf' : 'tag-img'}`}>
            {esPdf ? '📕 PDF' : '🖼 IMG'}
          </span>
          {item.archivo?.sizeKb > 0 && (
            <span className="descarga-tamano">{fmtSize(item.archivo.sizeKb)}</span>
          )}
        </div>
      </div>

      <a
        className="descarga-btn"
        href={item.archivo?.url}
        download={item.archivo?.nombreOriginal || item.titulo}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onDescargar}
      >
        <i className="pi pi-download" /> Descargar
      </a>
    </article>
  );
}
